import React, { useState, useEffect } from 'react';
import { humanize, titleize } from 'underscore.string';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import CargomaticModal from 'components/v2/modal';
import CargomaticInput from 'components/v2/input';
import CargomaticRadio from 'components/v2/forms/Radio';
import { sluggify } from 'utils/localization/utils';
const ReviewBankingModal = ({ partnerFactoringCompanyName, partnerWithFactoringCompany, sensitiveDocumentData, show, onClose, onConfirmDetails, onDeclineDetails, onGetVerificationData, }) => {
    const { t } = useTranslation();
    const [partnerCompany, setPartnerCompany] = useState(partnerFactoringCompanyName || '');
    const [partnerCompanyChecked, setPartnerCompanyChecked] = useState(partnerWithFactoringCompany || false);
    const [documentFetched, setDocumentFetched] = useState(false);
    const getVerificationData = async () => {
        await onGetVerificationData().catch((e) => {
            return console.log(e);
        });
        setDocumentFetched(true);
    };
    useEffect(() => {
        getVerificationData();
    }, []);
    const handlePartnerCompanyCheckboxChange = () => {
        setPartnerCompanyChecked((prev) => !prev);
    };
    const handlePartnerCompanyChange = (partnerCompany) => {
        setPartnerCompany(partnerCompany || '');
    };
    const handleConfirmBankingDetails = () => {
        onConfirmDetails();
        onClose();
    };
    const handleDeclineBankingDetails = () => {
        onDeclineDetails();
        onClose();
    };
    const openAllDocumentLinks = () => {
        if (sensitiveDocumentData) {
            const documentLinks = sensitiveDocumentData.reduce((documentArray, currentDoc) => {
                return currentDoc.url
                    ? documentArray.concat(currentDoc.url)
                    : documentArray;
            }, []);
            documentLinks.forEach((documentLink) => {
                window.open(documentLink, '_blank');
            });
        }
    };
    const getVerificationDocumentList = () => {
        if (!sensitiveDocumentData || sensitiveDocumentData.length === 0) {
            if (documentFetched) {
                return React.createElement("h5", null, t('NO_DOCUMENTS_UPLOADED'));
            }
            return React.createElement("h5", null,
                t('FETCHING_DOCUMENTS'),
                "...");
        }
        const groupedDocuments = {
            w9: [],
            voided_check: [],
            notice_of_assignment: [],
        };
        sensitiveDocumentData.forEach((document) => {
            var _a, _b, _c;
            if (((_a = document.metadata) === null || _a === void 0 ? void 0 : _a.document_type) === 'w9') {
                groupedDocuments.w9.push(document);
            }
            else if (((_b = document.metadata) === null || _b === void 0 ? void 0 : _b.document_type) === 'voided_check') {
                groupedDocuments.voided_check.push(document);
            }
            else if (((_c = document.metadata) === null || _c === void 0 ? void 0 : _c.document_type) === 'notice_of_assignment') {
                groupedDocuments.notice_of_assignment.push(document);
            }
        });
        return (React.createElement("div", { className: "grid grid-cols-2 gap-4 justify-items-start mx-3" }, Object.keys(groupedDocuments).map((documentType) => {
            if (!groupedDocuments[documentType].length)
                return null;
            return (React.createElement("div", { key: documentType },
                React.createElement("h6", { className: "my-3" }, t(sluggify(humanize(documentType)))),
                React.createElement("div", { className: "grid content-center gap-4 grid-cols-2 justify-items-start" }, groupedDocuments[documentType].map((document, documentIndex) => {
                    return (React.createElement("a", { key: documentIndex, href: document.url || '', target: "_blank", rel: "noreferrer" }, `Document ${documentIndex + 1}`));
                }))));
        })));
    };
    const getBody = () => {
        return (React.createElement(React.Fragment, null,
            partnerCompanyChecked && (React.createElement("div", { className: "max-w-sm pb-2 m-3 form-label" },
                React.createElement(CargomaticRadio, { id: "partner_company_checkbox", className: "my-3", checked: partnerCompanyChecked, disabled: true, name: "partnerCompanyRadio", value: partnerCompanyChecked, onChange: () => { } }, t('PARTNER_WITH_FACTORING_COMPANY')),
                React.createElement(CargomaticInput, { name: "partnerCompany", value: partnerCompany, onChange: handlePartnerCompanyChange, type: "text", className: "mb-4 mx-4", placeholder: t('PARTNER_COMPANY_NAME'), disabled: true }))),
            React.createElement("div", { className: "flex mx-3 gap-4 items-center" },
                React.createElement("h4", null, t('DOCUMENTS_UPLOADED')),
                (sensitiveDocumentData === null || sensitiveDocumentData === void 0 ? void 0 : sensitiveDocumentData.length) && (React.createElement("a", { className: "underline cursor-pointer", onClick: openAllDocumentLinks }, t('OPEN_ALL_DOCUMENTS')))),
            getVerificationDocumentList()));
    };
    const getFooter = () => {
        return (React.createElement("div", { className: "flex justify-end items-center space-x-2" },
            React.createElement("button", { className: "button button-green", onClick: handleConfirmBankingDetails }, t('CONFIRM_BANKING_INFORMATION')),
            React.createElement("button", { className: "button button-secondary", onClick: handleDeclineBankingDetails }, t('DECLINE'))));
    };
    return (React.createElement(CargomaticModal, { show: show, title: titleize(t('BANKING_INFORMATION')), body: getBody(), footer: getFooter(), onHide: onClose }));
};
ReviewBankingModal.propTypes = {
    show: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    partnerWithFactoringCompany: PropTypes.bool,
    partnerFactoringCompanyName: PropTypes.string,
    onConfirmDetails: PropTypes.func.isRequired,
    onDeclineDetails: PropTypes.func.isRequired,
    sensitiveDocumentData: PropTypes.array,
    onGetVerificationData: PropTypes.func,
};
export default ReviewBankingModal;
